<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Snippets
    </h2>

    <TabView v-model:activeIndex="activeTabIndex">
      <TabPanel header="Library">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-3"
            >
              <Button
                icon="pi pi-refresh"
                text
                rounded
                aria-label="Refresh"
                v-tooltip.bottom="'Refresh'"
                :loading="loading"
                @click="fetchSnippets"
              />
              <div class="flex flex-column sm:flex-row gap-3">
                <div class="flex align-items-center gap-1 w-full sm:w-20rem">
                  <Calendar
                    input-id="snippets-date-range"
                    v-model="dateRange"
                    :showIcon="true"
                    :number-of-months="1"
                    :selectionMode="'range'"
                    placeholder="Search by date range"
                    class="w-full"
                    @update:modelValue="setDateRange"
                  />
                  <i
                    v-if="dateRange"
                    class="pi pi-times cursor-pointer"
                    v-tooltip.bottom="'Clear date range'"
                    @click="clearDateRange"
                  ></i>
                </div>
                <InputText
                  class="w-full sm:w-24rem"
                  type="text"
                  v-model="searchQuery"
                  @input="onSearchInput"
                  placeholder="Search show or episode"
                />
              </div>
            </div>
          </template>
          <template #content>
            <DataTable
              :value="snippets"
              class="p-datatable-sm"
              lazy
              :paginator="true"
              :rows="10"
              :total-records="totalSnippets"
              :loading="loading"
              @page="onPage"
            >
              <Column field="id" header="ID">
                <template #body="slotProps">
                  <span class="text-500 text-sm">{{ slotProps.data.id }}</span>
                </template>
              </Column>

              <Column field="show_title" header="Show" />

              <Column field="episode_title" header="Episode">
                <template #body="slotProps">
                  <span
                    class="episode-title-cell"
                    v-tooltip.top="slotProps.data.episode_title"
                  >
                    {{ slotProps.data.episode_title }}
                  </span>
                </template>
              </Column>

              <Column header="Audio">
                <template #body="slotProps">
                  <audio
                    controls
                    preload="none"
                    class="snippet-audio-player"
                    :src="slotProps.data.snippet_audio_url"
                  />
                </template>
              </Column>

              <Column field="duration_sec" header="Duration">
                <template #body="slotProps">
                  {{ slotProps.data.duration_sec }}s
                </template>
              </Column>

              <Column header="Transcript">
                <template #body="slotProps">
                  <Button
                    v-if="slotProps.data.metadata?.transcript_text"
                    label="View"
                    size="small"
                    text
                    @click="openTranscript(slotProps.data)"
                  />
                  <span v-else class="text-500 text-sm">None</span>
                </template>
              </Column>

              <Column field="created_at" header="Created At">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.created_at) }}
                </template>
              </Column>

              <Column header="Actions">
                <template #body="slotProps">
                  <RegenerationStatusCell
                    :regenerating="slotProps.data.regenerating"
                    :just-regenerated="
                      justRegeneratedIds.has(slotProps.data.id)
                    "
                    :loading="regeneratingClickedId === slotProps.data.id"
                    @regenerate="confirmRegenerateSnippet(slotProps.data)"
                  />
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>

      <TabPanel header="Shows">
        <Card>
          <template #content>
            <DataTable
              :value="shows"
              class="p-datatable-sm"
              :paginator="true"
              :rows="10"
              :loading="showsLoading"
            >
              <template #header>
                <div class="flex justify-content-end gap-2">
                  <Button
                    icon="pi pi-refresh"
                    text
                    rounded
                    aria-label="Refresh"
                    v-tooltip.bottom="'Refresh'"
                    @click="fetchShows"
                  />
                  <Button
                    label="Add Show"
                    icon="pi pi-plus"
                    size="small"
                    @click="openAddShowDialog"
                  />
                </div>
              </template>

              <Column field="show_title" header="Show" :sortable="true" />

              <Column
                field="snippet_count"
                header="Snippets Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.snippet_count > 0 ? 'success' : 'secondary'
                    "
                    :value="slotProps.data.snippet_count"
                  />
                </template>
              </Column>

              <Column
                field="last_snippet_at"
                header="Last Snippet Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <span v-if="slotProps.data.last_snippet_at">
                    {{ formatDate(slotProps.data.last_snippet_at) }}
                  </span>
                  <span v-else class="text-500 text-sm">Never</span>
                </template>
              </Column>

              <Column
                field="active_seller_count"
                header="Active Sellers"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.active_seller_count > 0
                        ? 'info'
                        : 'secondary'
                    "
                    :value="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column>
                <template #body="slotProps">
                  <Button
                    icon="pi pi-eye"
                    v-tooltip.left="
                      slotProps.data.snippet_count > 0
                        ? 'View Snippets'
                        : 'No snippets generated yet'
                    "
                    outlined
                    rounded
                    size="small"
                    class="mr-2"
                    :disabled="slotProps.data.snippet_count === 0"
                    @click="viewSnippetsForShow(slotProps.data)"
                    aria-label="View Snippets"
                  />
                  <Button
                    icon="pi pi-plus"
                    v-tooltip.left="'Generate One More Snippet'"
                    outlined
                    rounded
                    severity="success"
                    size="small"
                    class="mr-2"
                    :loading="
                      generatingOneMoreShowId === slotProps.data.show_id
                    "
                    @click="confirmGenerateOneMoreSnippet(slotProps.data)"
                    aria-label="Generate One More Snippet"
                  />
                  <Button
                    icon="pi pi-trash"
                    v-tooltip.left="
                      slotProps.data.active_seller_count > 0
                        ? 'Cannot remove: sellers still active for this show'
                        : 'Remove Show'
                    "
                    outlined
                    rounded
                    severity="danger"
                    size="small"
                    :disabled="slotProps.data.active_seller_count > 0"
                    @click="confirmRemoveShow(slotProps.data)"
                    aria-label="Remove Show"
                  />
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>
    </TabView>

    <TranscriptDialog
      v-model:visible="showTranscriptDialog"
      :text="selectedTranscript"
    />

    <Dialog
      v-model:visible="showAddShowDialog"
      header="Add Show"
      modal
      :style="{ width: '30rem' }"
    >
      <div class="flex flex-column gap-1">
        <label for="addShowDropdown">Show</label>
        <Dropdown
          id="addShowDropdown"
          v-model="showToAdd"
          :options="availableShowsToAdd"
          optionLabel="show_title"
          filter
          placeholder="Select a show"
          class="w-full"
          :panelStyle="{
            width: 'min(26rem, 96vw)',
            maxHeight: '16rem',
            overflow: 'hidden',
          }"
          :loading="availableShowsLoading"
        />
      </div>
      <template #footer>
        <Button
          label="Cancel"
          outlined
          :disabled="addShowSubmitting"
          @click="showAddShowDialog = false"
        />
        <Button
          label="Add"
          :loading="addShowSubmitting"
          :disabled="!showToAdd"
          @click="confirmAddShow"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Badge from "primevue/badge";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Calendar from "primevue/calendar";
import { formatDate } from "../../helpers";
import TranscriptDialog from "./TranscriptDialog.vue";
import RegenerationStatusCell from "./shared/RegenerationStatusCell.vue";
import { useSnippetsLibrary } from "./composables/useSnippetsLibrary";
import { useSnippetShows } from "./composables/useSnippetShows";

export default {
  components: {
    Card,
    DataTable,
    Column,
    Button,
    Dialog,
    InputText,
    Dropdown,
    Badge,
    TabView,
    TabPanel,
    Calendar,
    TranscriptDialog,
    RegenerationStatusCell,
  },
  setup() {
    const token = localStorage.getItem("adminToken");
    const activeTabIndex = ref(0);

    const library = useSnippetsLibrary(token);
    const showsTab = useSnippetShows(token);

    const viewSnippetsForShow = (show) => {
      library.searchQuery.value = show.show_title;
      library.currentPage.value = 1;
      activeTabIndex.value = 0;
    };

    watch(activeTabIndex, (index) => {
      if (index === 0) {
        library.fetchSnippets();
      } else if (index === 1) {
        library.resetFilters();
        showsTab.fetchShows();
      }
    });

    onMounted(() => {
      if (activeTabIndex.value === 0) library.fetchSnippets();
      else showsTab.fetchShows();
    });

    return {
      activeTabIndex,
      viewSnippetsForShow,
      formatDate,
      ...library,
      ...showsTab,
    };
  },
};
</script>

<style scoped>
:deep(.p-calendar .p-datepicker-trigger) {
  background: transparent;
  border: 1px solid #ced4da;
  border-left: none;
  color: #6c757d;
}

:deep(.p-calendar .p-datepicker-trigger:hover) {
  background: rgba(0, 0, 0, 0.04);
  color: #495057;
}

.snippet-audio-player {
  height: 2.25rem;
  max-width: 16rem;
}

.episode-title-cell {
  display: inline-block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
